import React, { useState } from 'react';
import ConsultationModal from '../components/ConsultationModal';
import img5 from '../img/5.png';

function Services() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [interest, setInterest] = useState('');

  const openBooking = (serviceName) => {
    setInterest(serviceName);
    setIsModalOpen(true);
  };

  const services = [
    {
      title: 'Hair Replacement Consultation',
      eyebrow: 'Start here',
      description:
        'A private consultation to understand your hair loss concerns, lifestyle, desired look, and the type of hair replacement solution that fits your needs.',
      highlights: ['Private evaluation', 'Lifestyle review', 'Solution guidance'],
    },
    {
      title: 'Custom Hair Systems',
      eyebrow: 'Personalized design',
      description:
        'Custom hair systems designed around your head shape, coverage needs, hair color, density, texture, and preferred finished style.',
      highlights: ['Custom base options', 'Natural density', 'Color and texture match'],
    },
    {
      title: 'Hair System Fitting',
      eyebrow: 'Secure fit',
      description:
        'Professional fitting to place and secure your hair system comfortably while creating a natural-looking hairline and balanced coverage.',
      highlights: ['Comfort-focused fit', 'Natural placement', 'Secure attachment'],
    },
    {
      title: 'Cut-In and Blending',
      eyebrow: 'Natural finish',
      description:
        'Detailed cut-in and blending so your hair system works with your facial features, existing hair, and everyday grooming routine.',
      highlights: ['Custom shaping', 'Seamless blend', 'Wear-ready finish'],
    },
    {
      title: 'Maintenance and Reattachment',
      eyebrow: 'Ongoing care',
      description:
        'Routine maintenance, cleaning, adjustment, and reattachment services to keep your hair replacement system looking fresh and secure.',
      highlights: ['System cleaning', 'Reattachment', 'Fit adjustments'],
    },
    {
      title: 'Hair System Care Education',
      eyebrow: 'At-home confidence',
      description:
        'Clear guidance on daily care, washing, styling, sleeping, products, and appointment timing so you can protect your investment.',
      highlights: ['Care routine', 'Product guidance', 'Wear schedule'],
    },
  ];

  const processSteps = [
    {
      title: 'Consult',
      description: 'We discuss your goals, coverage needs, lifestyle, and expectations in a private setting.',
    },
    {
      title: 'Design',
      description: 'We choose the system type, color, density, texture, and attachment approach that fits you.',
    },
    {
      title: 'Fit',
      description: 'We place, secure, cut in, and blend the system for a natural finished result.',
    },
    {
      title: 'Maintain',
      description: 'We keep your system clean, secure, and refreshed with scheduled maintenance.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="page-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={img5}
            alt="Hair replacement consultation and fitting"
            className="h-full w-full object-cover object-top opacity-40"
          />
          <div className="page-hero-scrim" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-3xl">
            <p className="section-kicker text-primary-200">Hair replacement only</p>
            <h1 className="mb-6 font-display text-5xl font-semibold leading-tight md:text-6xl">
              Hair Replacement Solutions
            </h1>
            <p className="max-w-2xl text-xl leading-relaxed text-white/85">
              Custom systems, fitting, cut-in, blending, maintenance, and care education for clients who want a natural-looking result.
            </p>
            <button
              type="button"
              onClick={() => openBooking('Hair Replacement Consultation')}
              className="mt-8 rounded-full bg-white px-8 py-3 font-semibold text-primary-700 shadow-lg transition duration-300 hover:bg-primary-50"
            >
              Schedule Your Consultation
            </button>
          </div>
        </div>
      </section>

      <section className="border-b border-primary-100 bg-white py-16">
        <div className="container mx-auto grid gap-10 px-4 md:grid-cols-[0.9fr_1.1fr] md:items-center">
          <div>
            <p className="section-kicker">Focused expertise</p>
            <h2 className="font-display text-4xl font-semibold text-ink">
              Every service supports your hair replacement result.
            </h2>
          </div>
          <p className="text-lg leading-relaxed text-ink-muted">
            Free consultations. Clear recommendations. No pressure to decide on the spot — just a private
            plan for selecting, wearing, and maintaining a custom hair system with confidence.
          </p>
        </div>
      </section>

      <section className="bg-surface py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 max-w-3xl">
            <p className="section-kicker">Services</p>
            <h2 className="section-heading">What We Offer</h2>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {services.map((service, index) => (
              <article
                key={service.title}
                className="service-card border border-primary-100 bg-white p-8 transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{ animationDelay: `${index * 90}ms` }}
              >
                <div className="mb-5 flex items-start justify-between gap-6">
                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-primary-600">
                      {service.eyebrow}
                    </p>
                    <h3 className="font-display text-2xl font-semibold text-ink">{service.title}</h3>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-bold text-primary-700">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <p className="mb-6 leading-relaxed text-ink-muted">{service.description}</p>
                <ul className="mb-6 grid gap-3 sm:grid-cols-3">
                  {service.highlights.map((highlight) => (
                    <li key={highlight} className="bg-surface px-4 py-3 text-sm font-medium text-ink-soft">
                      {highlight}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => openBooking(service.title)}
                  className="font-semibold text-primary-700 transition-colors hover:text-primary-900"
                >
                  Book for this →
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <p className="section-kicker">Our process</p>
            <h2 className="section-heading">How We Build Your Solution</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-4">
            {processSteps.map((step, index) => (
              <div key={step.title} className="border-t-2 border-primary-600 pt-6">
                <span className="mb-4 block text-sm font-bold text-primary-600">
                  Step {index + 1}
                </span>
                <h3 className="mb-3 font-display text-2xl font-semibold text-ink">{step.title}</h3>
                <p className="leading-relaxed text-ink-muted">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-700 py-16 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-5 font-display text-3xl font-semibold md:text-4xl">
            Ready for a custom hair replacement solution?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-primary-50">
            Schedule a consultation and we will help you choose a system, fit, maintenance plan, and care routine.
          </p>
          <button
            type="button"
            onClick={() => openBooking('Hair Replacement Consultation')}
            className="rounded-full bg-white px-8 py-3 font-semibold text-primary-700 shadow-lg transition duration-300 hover:bg-primary-50"
          >
            Book Consultation
          </button>
        </div>
      </section>

      <ConsultationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        interest={interest}
      />
    </div>
  );
}

export default Services;
